import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { NewsManagementService } from "@/services/news/newsManagementService";
import {
  NewsItem,
  CreateNewsRequest,
  UpdateNewsRequest,
  GetAllNewsParams,
  UpdateNewsImageRequest,
  RemoveNewsImageRequest,
} from "@/types/news";
import { FileObject } from "@/types/shared/file";

export const useNewsManagement = (params?: GetAllNewsParams) => {
  const queryClient = useQueryClient();

  // Get all news query with server-side pagination support
  const isEnabled = !!params && !!params.userId && params.userId.trim() !== "";

  // Use pagination params if provided, otherwise default to page 1 with limit 10
  const page = params?.page ?? 1;
  const limit = params?.limit ?? 10;

  const newsQuery = useQuery({
    queryKey: ["news", "admin", params?.userId, page, limit], // Include page and limit in key for proper caching
    queryFn: async () => {
      if (!params) {
        return { items: [], total: 0, pagination: undefined };
      }
      try {
        const result = await NewsManagementService.getAllNews({
          ...params,
          page,
          limit,
        });
        const returnValue = result || {
          items: [],
          total: 0,
          pagination: undefined,
        };
        return returnValue;
      } catch (error) {
        return { items: [], total: 0, pagination: undefined };
      }
    },
    enabled: isEnabled, // Only enable if params and userId exist and is not empty
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 1, // Retry once on failure
    refetchOnMount: "always", // Always refetch when component mounts to get latest news
    refetchOnWindowFocus: false, // Don't refetch on window focus (only on mount)
  });

  // Create news mutation
  const createNewsMutation = useMutation({
    mutationFn: ({
      request,
      createdBy,
    }: {
      request: CreateNewsRequest;
      createdBy: string;
    }) => NewsManagementService.createNews(request, createdBy),
    onSuccess: () => {
      // Invalidate all news queries to refresh the list
      queryClient.invalidateQueries({ queryKey: ["news"] });
      // Also invalidate admin news queries specifically
      queryClient.invalidateQueries({ queryKey: ["news", "admin"] });
    },
  });

  // Update news mutation
  const updateNewsMutation = useMutation({
    mutationFn: (request: UpdateNewsRequest) =>
      NewsManagementService.updateNews(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["news"] });
    },
  });

  // Update news image mutation
  const updateNewsImageMutation = useMutation({
    mutationFn: ({
      newsId,
      request,
      createdBy,
    }: {
      newsId: string;
      request: UpdateNewsImageRequest;
      createdBy: string;
    }) => NewsManagementService.updateNewsImage(newsId, request, createdBy),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["news"] });
    },
  });

  // Remove news image mutation
  const removeNewsImageMutation = useMutation({
    mutationFn: (request: RemoveNewsImageRequest) =>
      NewsManagementService.removeNewsImage(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["news"] });
    },
  });

  // Delete news mutation
  const deleteNewsMutation = useMutation({
    mutationFn: (newsId: string) => NewsManagementService.removeNews(newsId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["news"] });
    },
  });

  // Publish news mutation
  const publishNewsMutation = useMutation({
    mutationFn: (newsId: string) => NewsManagementService.publishNews(newsId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["news"] });
    },
  });

  // Hide news mutation
  const hideNewsMutation = useMutation({
    mutationFn: (newsId: string) => NewsManagementService.hideNews(newsId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["news"] });
    },
  });

  return {
    // Data - extract items from the response object
    news: newsQuery.data?.items || [],

    // Pagination data
    total: newsQuery.data?.total || 0,
    pagination: newsQuery.data?.pagination,

    // Loading states
    isLoading: newsQuery.isLoading,

    // Error states
    error: newsQuery.error?.message || null,

    // Mutations
    createNews: createNewsMutation.mutateAsync,
    updateNews: updateNewsMutation.mutateAsync,
    updateNewsImage: updateNewsImageMutation.mutateAsync,
    removeNewsImage: removeNewsImageMutation.mutateAsync,
    deleteNews: deleteNewsMutation.mutateAsync,
    removeNews: deleteNewsMutation.mutateAsync,
    publishNews: publishNewsMutation.mutateAsync,
    hideNews: hideNewsMutation.mutateAsync,

    // Mutation states
    isCreating: createNewsMutation.isPending,
    isUpdating: updateNewsMutation.isPending,
    isUpdatingImage: updateNewsImageMutation.isPending,
    isRemovingImage: removeNewsImageMutation.isPending,
    isDeleting: deleteNewsMutation.isPending,

    // Mutation errors
    createError: createNewsMutation.error?.message || null,
    updateError: updateNewsMutation.error?.message || null,
    updateImageError: updateNewsImageMutation.error?.message || null,
    removeImageError: removeNewsImageMutation.error?.message || null,
    deleteError: deleteNewsMutation.error?.message || null,

    // Mutation success states
    createSuccess: createNewsMutation.isSuccess,
    updateSuccess: updateNewsMutation.isSuccess,
    updateImageSuccess: updateNewsImageMutation.isSuccess,
    removeImageSuccess: removeNewsImageMutation.isSuccess,
    deleteSuccess: deleteNewsMutation.isSuccess,

    // Reset functions
    resetCreate: createNewsMutation.reset,
    resetUpdate: updateNewsMutation.reset,
    resetUpdateImage: updateNewsImageMutation.reset,
    resetRemoveImage: removeNewsImageMutation.reset,
    resetDelete: deleteNewsMutation.reset,
  };
};
